import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, DeleteModal, EditButton, ToggleButton } from "../../components";
import styles from "./Table.module.css";

function TestimonialsTable({ data, handleEdit, handleDelete }) {
  const columns = [
    { id: "customer_photo", name: "Customer Photo" },
    { id: "customer_name", name: "Customer Name" },
    { id: "project_name", name: "Project Name" },
    { id: "review", name: "Testimonial" },
    { id: "rating", name: "No. of stars" },
    { id: "action", name: "Action" },
  ];

  const [tableData, setTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTestimonialId, setDeleteTestimonialId] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [editTestimonialId, setEditTestimonialId] = useState(null);
  const [editedReview, setEditedReview] = useState("");
  const [editedRating, setEditedRating] = useState(0);

  const handleDeleteTestimonial = () => {
    setShowDeleteModal(false);
    handleDelete(deleteTestimonialId);
  };

  const handleClickDelete = (id) => {
    if (!id) return;
    setShowDeleteModal(true);
    setDeleteTestimonialId(id);
  };

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedData = [...tableData].sort((a, b) => {
      const parseValue = (value) =>
        typeof value === "string" && value.includes("₹") ? parseInt(value.replace(/₹|,/g, "")) : value;
      const aValue = parseValue(a[key]) ?? "";
      const bValue = parseValue(b[key]) ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setTableData(sortedData);
  };

  const handleEditButtonClick = (id) => {
    setIsEditable(true);
    setEditTestimonialId(id);
    const selectedReview = tableData.find((row) => row.testimonial_id === id);
    const initialReview = selectedReview?.review;
    const initialRating = selectedReview?.rating;

    if (!initialReview || !initialRating) return;
    setEditedReview(initialReview);
    setEditedRating(initialRating);
  };

  return (
    <>
      <div className={`${styles.table} ${styles.table_6} ${styles.spread}`}>
        <div className={styles.table_header}>
          {columns.map((column) => (
            <div key={column.id} className={styles.table_header_cell}>
              <span className={styles.table_header_name}>{column.name}</span>
              {column.id !== "action" && column.id !== "customer_photo" && column.id !== "review" && (
                <ToggleButton
                  onClick={() => handleSort(column.id)}
                  open={sortConfig.key !== column.id || sortConfig.direction === "desc"}
                />
              )}
            </div>
          ))}
        </div>
        <div className={styles.table_body}>
          {tableData.length === 0 && <div className="space-top">No data available</div>}
          {tableData.map((row) => (
            <div key={row.testimonial_id} className={`${styles.table_row} ${styles.center}`}>
              {columns.map((column) => (
                <div key={column.id} className={styles.table_body_cell}>
                  {column.id === "customer_photo" ? (
                    row.customer_photo?.url ? (
                      <img src={row.customer_photo?.url} alt="" className={styles.customer_photo} />
                    ) : (
                      <Icon icon="fluent-emoji-flat:man-medium" className={styles.photo_placeholder} />
                    )
                  ) : column.id === "action" ? (
                    isEditable && editTestimonialId === row.testimonial_id ? (
                      <div className="row">
                        <Button
                          onClick={() => {
                            setIsEditable(false);
                            setEditedRating("");
                            setEditedReview("");
                            setEditTestimonialId(null);
                          }}
                          className="ghost_gray"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            handleEdit(editTestimonialId, {
                              review: editedReview,
                              rating: +editedRating,
                            });
                            setIsEditable(false);
                            setEditedRating("");
                            setEditedReview("");
                            setEditTestimonialId(null);
                          }}
                          className="ghost_gray"
                        >
                          Save
                        </Button>
                      </div>
                    ) : (
                      <div className="row">
                        <EditButton
                          onClick={() => handleEditButtonClick(row.testimonial_id)}
                          icon="line-md:edit"
                          iconPosition="start"
                          className="ghost_gray"
                        />
                        <Button
                          onClick={() => handleClickDelete(row.testimonial_id)}
                          icon="ic:outline-delete"
                          iconPosition="start"
                          className="ghost_gray"
                        >
                          Delete
                        </Button>
                      </div>
                    )
                  ) : column.id === "review" ? (
                    isEditable && editTestimonialId === row.testimonial_id ? (
                      <textarea
                        name="testimonial"
                        id="testimonial"
                        className="input_textarea full"
                        value={editedReview}
                        onChange={(e) => setEditedReview(e.target.value)}
                        rows={1}
                        maxLength={600}
                      ></textarea>
                    ) : (
                      <span className="testimonial_view">{row.review}</span>
                    )
                  ) : column.id === "rating" ? (
                    isEditable && editTestimonialId === row.testimonial_id ? (
                      <input
                        type="number"
                        name="rating"
                        id="rating"
                        value={editedRating}
                        className="input"
                        min={0.5}
                        max={5}
                        step={0.5}
                        onChange={(e) => setEditedRating(e.target.value)}
                      />
                    ) : (
                      <span>{row.rating}</span>
                    )
                  ) : (
                    row[column.id]
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {showDeleteModal &&
        createPortal(
          <DeleteModal
            isVisible={showDeleteModal}
            handleClose={() => setShowDeleteModal(false)}
            handleDelete={handleDeleteTestimonial}
            message="Are you sure you want to delete this testimonial?"
          />,
          document.body
        )}
    </>
  );
}

export default TestimonialsTable;
